import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton,
  Drawer,
  Card,
  CardContent,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ChevronRight, ChevronLeft, Close, Description } from '@mui/icons-material';
import { PDFDocument } from 'pdf-lib';
import { fillPDFForm } from './WageRateNoticeFieldMappings';
import FormDebugOverlay from './FormDebugOverlay';
import { API_URL } from '../../../utils/apiConfig';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
  // PDFViewer component with fixed path handling
// Updated PDFViewer component
// PDFViewer component that matches the working W4 form implementation
const PDFViewer = ({ pdfUrl, height = '80vh' }) => (
  <Box sx={{ height, backgroundColor: 'background.paper', borderRadius: 1, overflow: 'hidden', mb: 3 }}>
    <iframe
      src={pdfUrl}
      style={{ width: '100%', height: '100%', border: 'none' }}
      title="Wage Rate Notice Form"
    />
  </Box>
);


const CompletedFormView = ({ 
  filePath, 
  onStartEdit,
  formTitle = "Wage Rate Notice Form"
}) => {
  const token = localStorage.getItem('token');
  
  if (!filePath) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Unable to load completed form. Please try again or contact support.
      </Alert>
    );
  }

  // Use the filePath directly if it's a blob URL, otherwise construct authenticated URL
  const authenticatedUrl = filePath.startsWith('blob:') ? filePath : `${API_URL.replace('/api', '')}/${filePath}?token=${token}`;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper 
        elevation={1}
        sx={{ 
          mb: 2,
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          borderRadius: 1
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e0e0e0',
            bgcolor: 'primary.light',
          }}
        >
          <Typography variant="h6" sx={{ color: 'black' }}>
            {formTitle}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={onStartEdit}
              sx={{ mr: 1 }}
            >
              Fill Form Again
            </Button>
            
          </Box>
        </Box>

        <PDFViewer pdfUrl={authenticatedUrl} />
      </Paper>
    </Box>
  );
};
  const WageRateNoticeForm = ({ 
    employeeId,
    formId,
    employee,
    employer,
    isCompleted: initialIsCompleted,
    documentStatus: initialDocumentStatus,
    onSubmit,
    filePath: initialFilePath,
  }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [employerInfo, setEmployerInfo] = useState({
    name: '',
    address: '',
    phone: '',
    EIN: ''
  });
  const [formData, setFormData] = useState({
    // Employer Information - now using employerInfo state
    employerName: '',
    dbaNames: '',
    fein: '',
    physicalAddress: '',
    mailingAddress: '',
    phone: '',

    // Employee Information
    employeeHourlyRate: '',  // Will be set by API
    regularPayday: 'Friday',
    payFrequency: 'weekly',
    overtimeRate: '',  // Will be calculated from API rate
    
    // Notice Type
    noticeType: 'hiring',
    
 
    
    // Allowances
    allowances: {
      none: true,
      tips: false,
      meals: false,
      lodging: false,
      other: false
    },
    allowanceAmounts: {
      tips: '',
      meals: '',
      lodging: '',
      other: ''
    },
    
    // Language Selection
    primaryLanguage: 'english',
    otherLanguage: '',
    
    // Employee Acknowledgement
    employeeName: employee?.fullName || '',
    signatureDate: new Date().toISOString().split('T')[0],
    legalAcknowledgment: false
  });


  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCompleted, setIsCompleted] = useState(initialIsCompleted);
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const iframeRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isEditing, setIsEditing] = useState(!initialIsCompleted);
  const [filePath, setFilePath] = useState(initialFilePath);
  const [documentStatus, setDocumentStatus] = useState(initialDocumentStatus);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('allowances.')) {
      const allowanceKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        allowances: {
          ...prev.allowances,
          [allowanceKey]: checked
        }
      }));
    } else if (name.startsWith('allowanceAmounts.')) {
      const amountKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        allowanceAmounts: {
          ...prev.allowanceAmounts,
          [amountKey]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };



  // Add handleStartEdit function
const handleStartEdit = () => {
  setIsEditing(true);
  setIsCompleted(false);
};



// Update handleSubmit function

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsLoading(true);
  setError(null);

  try {
    const token = localStorage.getItem('token');  

    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      throw new Error('Please provide a signature');
    }

    if (!formData.legalAcknowledgment) {
      throw new Error('Please acknowledge the legal statement before submitting');
    }

    const signatureImage = signaturePadRef.current.toDataURL('image/png');
    
    const pdfPath = '/static-forms/wage_rate_notice.pdf';
    const response = await fetch(pdfPath);
    
    if (!response.ok) {
      throw new Error(`Failed to load PDF template: ${response.statusText}`);
    }
    
    const pdfBytes = await response.arrayBuffer();
    const pdfDoc = await PDFDocument.load(pdfBytes);
    
    const filledPdfDoc = await fillPDFForm(pdfDoc, formData, signatureImage);
    const filledPdfBytes = await filledPdfDoc.save();
    const blob = new Blob([filledPdfBytes], { type: 'application/pdf' });
    
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('file', blob, `wage_rate_notice_${employeeId}.pdf`);
    formDataToSubmit.append('employeeId', employeeId);
    formDataToSubmit.append('formId', formId);
    formDataToSubmit.append('isResubmission', initialIsCompleted ? 'true' : 'false');
    
    const uploadResponse = await fetch(`${API_URL}/forms/submit-wage-rate-notice`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formDataToSubmit
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.statusText}`);
    }

    const result = await uploadResponse.json();

    if (onSubmit) {
      await onSubmit(formId, result.filePath);
    }

    setPdfUrl(result.filePath);
    setIsCompleted(true);
    setIsEditing(false);

  } catch (err) {
    setError(err.message);
  } finally {
    setIsLoading(false);
  }
};






useEffect(() => {
  const fetchPDF = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/forms/wage-rate-notice/${employeeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Fetch Error Text:', errorText);
        throw new Error(`Failed to fetch PDF: ${response.status} ${errorText}`);
      }

      const pdfBlob = await response.blob();
      const url = URL.createObjectURL(pdfBlob);

      if (url !== pdfUrl) {
        setPdfUrl(url);
        // Don't set filePath from blob URL
      }

    } catch (error) {
      console.error('Error fetching PDF:', error);
      setError(error.message || 'Failed to load wage rate notice form');
    }
  };

  const fetchEmployeeAndEmployerInfo = async () => {
    try {
      const token = localStorage.getItem('token');

      // Fetch employee pay rate
      const employeeResponse = await fetch(`${API_URL}/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!employeeResponse.ok) throw new Error('Failed to fetch employee pay rate');
      const employeeData = await employeeResponse.json();

      // Fetch employer info (robust):
      // 1) Prefer W4 JSON data which includes employer (server has fallback to default location)
      // 2) If missing/empty, try location-based endpoint when employee has locationId
      // 3) As a last resort in dev, try location id 1
      let location;

      // Step 1: W4 JSON data
      try {
        const w4JsonResp = await fetch(`${API_URL}/forms/w4/data/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
        });
        if (w4JsonResp.ok) {
          const w4Json = await w4JsonResp.json();
          if (w4Json && w4Json.employer && (w4Json.employer.name || w4Json.employer.address)) {
            location = w4Json.employer;
          }
        }
      } catch (_) {
        // ignore; will try next step
      }

      // Step 2: If still missing and employee has a locationId, try employer-by-location
      if (!location && employeeData.locationId) {
        try {
          const employerResp = await fetch(`${API_URL}/forms/w4/employer/${employeeData.locationId}`, {
            headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
          });
          if (employerResp.ok) {
            const locJson = await employerResp.json();
            if (locJson && (locJson.name || locJson.address)) {
              location = locJson;
            }
          }
        } catch (_) {
          // ignore; will try last resort
        }
      }

      // Step 3: Last-resort default (dev only): location id 1
      if (!location && process.env.NODE_ENV === 'development') {
        try {
          const defaultResp = await fetch(`${API_URL}/forms/w4/employer/1`, {
            headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
          });
          if (defaultResp.ok) {
            const defLoc = await defaultResp.json();
            if (defLoc && (defLoc.name || defLoc.address)) {
              location = defLoc;
            }
          }
        } catch (_) {
          // keep location undefined if this fails
        }
      }

      // If still nothing, keep existing values but set a user-friendly error
      if (!location) {
        setError('Employer information not available for this employee.');
      }

      // Update employer state if we have values; never set undefined
      if (location) {
        setEmployerInfo(prev => {
          const next = {
            name: location.name ?? prev.name ?? '',
            address: location.address ?? prev.address ?? '',
            phone: location.phone ?? prev.phone ?? '',
            EIN: (location.EIN ?? prev.EIN ?? '')
          };
          // Only trigger state update when something actually changed
          if (
            next.name !== prev.name ||
            next.address !== prev.address ||
            next.phone !== prev.phone ||
            next.EIN !== prev.EIN
          ) {
            return next;
          }
          return prev;
        });
      }

      // Always set pay rates; set employer fields only if we have them
      setFormData(prev => ({
        ...prev,
        employerName: location?.name ?? prev.employerName ?? '',
        dbaNames: location?.name ?? prev.dbaNames ?? '',
        fein: location?.EIN ?? prev.fein ?? '',
        physicalAddress: location?.address ?? prev.physicalAddress ?? '',
        mailingAddress: location?.address ?? prev.mailingAddress ?? '',
        phone: location?.phone ?? prev.phone ?? '',
        employeeHourlyRate: employeeData.payRate,
        overtimeRate: (employeeData.payRate * 1.5).toFixed(2),
      }));

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Failed to load employee and employer information');
    }
  };

  const syncFormStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/employee-documents/sync-status/${employeeId}/${formId}`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-cache' } }
      );

      if (!response.ok) throw new Error('Failed to sync form status');
      const data = await response.json();

      // Only update state if values have changed
      setIsCompleted(prev => (prev !== Boolean(data.isCompleted) ? Boolean(data.isCompleted) : prev));
      setDocumentStatus(prev => (prev !== data.documentStatus ? data.documentStatus : prev));
      setFilePath(prev => (prev !== data.filePath ? data.filePath : prev));
      // Don't set pdfUrl from filePath directly

    } catch (error) {
      console.error('Error syncing form status:', error);
    }
  };

  if (employeeId && isCompleted) {
    fetchPDF();
  }

  if (employeeId) {
    fetchEmployeeAndEmployerInfo();
  }

  if (employeeId && formId) {
    syncFormStatus();
  }

  return () => {
    if (pdfUrl && !filePath) {
      URL.revokeObjectURL(pdfUrl);
    }
  };
}, [employeeId, isCompleted, formId, employee?.locationId]); // Removed `filePath` to prevent infinite loop


useEffect(() => {
  const initializeSignaturePad = async () => {
    try {
      if (signaturePadRef.current) {
        return; // Prevent reinitialization
      }

      const SignaturePad = (await import('signature_pad')).default;

      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
          console.warn('Canvas element not found');
          return;
        }

        const parent = canvas.parentElement;
        if (!parent) {
          console.warn('Canvas parent element not found');
          return;
        }

        const parentWidth = parent.clientWidth || 300;

        // Adjust canvas size dynamically
        canvas.width = parentWidth;
        canvas.height = 150;

        // Initialize signature pad
        signaturePadRef.current = new SignaturePad(canvas, {
          backgroundColor: 'rgb(255, 255, 255)',
          penColor: 'rgb(0, 0, 0)',
        });
      }, 0);
    } catch (error) {
      console.error('Error initializing signature pad:', error);
    }
  };

  initializeSignaturePad();

  const handleResize = () => {
    if (canvasRef.current && signaturePadRef.current) {
      const canvas = canvasRef.current;
      const parent = canvas.parentElement;
      if (!parent) return;

      const parentWidth = parent.clientWidth || 300;

      // Save signature data before resizing
      const signatureData = signaturePadRef.current.toData();

      canvas.width = parentWidth;
      canvas.height = 150;

      signaturePadRef.current.clear();
      if (signatureData.length > 0) {
        signaturePadRef.current.fromData(signatureData);
      }
    }
  };

  window.addEventListener('resize', handleResize);

  return () => {
    window.removeEventListener('resize', handleResize);
    if (signaturePadRef.current) {
      signaturePadRef.current.off();
    }
  };
}, []);

  const getDrawerWidth = () => {
    if (isMobile) return '100%';
    return '600px';
  };

  const togglePreview = () => {
    setIsPreviewOpen(!isPreviewOpen);
  };
 // In WageRateNoticeForm component, update the completed form check:
 if (isCompleted && !isEditing) {
  if (!filePath && !pdfUrl) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 3 }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }} color="textSecondary">
          Loading completed form...
        </Typography>
      </Box>
    );
  }

  return (
    <CompletedFormView
      filePath={pdfUrl || filePath}
      onStartEdit={() => {
        setIsEditing(true);
        setIsCompleted(false);
      }}
      formTitle="Wage Rate Notice Form"
    />
  );
}


  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      <Box sx={{ flex: 1 }}>
   

    {/* Notice Text Section */}
    <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff5b3', borderRadius: 1, textAlign: 'center', mb:1, border: '2px solid rgb(231, 164, 164)' }}>
      <Typography variant="subtitle1" fontWeight="bold">
        Notice and Acknowledgement of Pay Rate and Payday
      </Typography>
      <Typography variant="body1">
        <a href="https://dol.ny.gov/notice-pay-rate" target="_blank" rel="noopener noreferrer">Section 195.1 of the New York State Labor Law Under Section 195.1 of the New York State Labor Law</a>
      </Typography>
      <Typography variant="body1">
        Notice for Hourly Rate Employee
      </Typography>

          <IconButton 
            onClick={togglePreview}
            sx={{ 
              position: 'fixed',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 1,
              zIndex: 2000,
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}
          >
            {isPreviewOpen ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
         <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            1. Employer Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employer Name"
                value={employerInfo.name}
                InputProps={{ readOnly: true }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employer's Address"
                value={employerInfo.address}
                InputProps={{ readOnly: true }}
                variant="filled"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employer's Phone"
                value={employerInfo.phone}
                InputProps={{ readOnly: true }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Federal Employer Identification Number (FEIN)"
                value={employerInfo.EIN}
                InputProps={{ readOnly: true }}
                variant="filled"
              />
            </Grid>
          </Grid>


        </CardContent>
      </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                2. Notice Type
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  name="noticeType"
                  value={formData.noticeType}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="hiring"
                    control={<Radio />}
                    label="At hiring"
                  />
                  <FormControlLabel
                    value="change"
                    control={<Radio />}
                    label="Before a change in pay rate(s)"
                  />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
  <CardContent>
    <Typography variant="h6" gutterBottom>
      3. Pay Information
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Hourly Rate ($)"
          name="employeeHourlyRate"
          type="number"
          value={formData.employeeHourlyRate}
          InputProps={{ readOnly: true }}
          variant="filled"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Overtime Rate ($)"
          name="overtimeRate"
          type="number"
          value={formData.overtimeRate}
          InputProps={{ readOnly: true }}
          variant="filled"
        />
      </Grid>
    </Grid>
  </CardContent>
</Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                4. Allowances
              </Typography>
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.allowances.none}
                      onChange={handleInputChange}
                      name="allowances.none"
                    />
                  }
                  label="None"
                />
                
                {!formData.allowances.none && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.allowances.tips}
                            onChange={handleInputChange}
                            name="allowances.tips"
                          />
                        }
                        label="Tips"
                      />
                      {formData.allowances.tips && (
                        <TextField
                          fullWidth
                          label="Amount per hour"
                          name="allowanceAmounts.tips"
                          type="number"
                          inputProps={{ step: "0.01" }}
                          value={formData.allowanceAmounts.tips}
                          onChange={handleInputChange}
                        />
                      )}
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.allowances.meals}
                            onChange={handleInputChange}
                            name="allowances.meals"
                          />
                        }
                        label="Meals"
                      />
                      {formData.allowances.meals && (
                        <TextField
                          fullWidth
                          label="Amount per meal"
                          name="allowanceAmounts.meals"
                          type="number"
                          inputProps={{ step: "0.01" }}
                          value={formData.allowanceAmounts.meals}
                          onChange={handleInputChange}
                        />
                      )}
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.allowances.lodging}
                            onChange={handleInputChange}
                            name="allowances.lodging"
                          />
                        }
                        label="Lodging"
                      />
                      {formData.allowances.lodging && (
                        <TextField
                          fullWidth
                          label="Amount"
                          name="allowanceAmounts.lodging"
                          type="number"
                          inputProps={{ step: "0.01" }}
                          value={formData.allowanceAmounts.lodging}
                          onChange={handleInputChange}
                        />
                      )}
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.allowances.other}
                            onChange={handleInputChange}
                            name="allowances.other"
                          />
                        }
                        label="Other"
                      />
                      {formData.allowances.other && (
                        <TextField
                          fullWidth
                          label="Amount"
                          name="allowanceAmounts.other"
                          type="number"
                          inputProps={{ step: "0.01" }}
                          value={formData.allowanceAmounts.other}
                          onChange={handleInputChange}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
              </FormControl>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
  <CardContent>
    <Typography variant="h6" gutterBottom>
      5. Pay Schedule
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Regular Payday"
          value="Friday"
          InputProps={{ readOnly: true }}
          variant="filled"
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <Typography variant="subtitle1" gutterBottom>
            Pay Frequency
          </Typography>
          <TextField
            fullWidth
            value="Weekly"
            InputProps={{ readOnly: true }}
            variant="filled"
          />
        </FormControl>
      </Grid>
    </Grid>
  </CardContent>
</Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                6. Language Selection
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  name="primaryLanguage"
                  value={formData.primaryLanguage}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="english"
                    control={<Radio />}
                    label="English is my primary language"
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label="My primary language is:"
                  />
                </RadioGroup>
              </FormControl>
              
              {formData.primaryLanguage === 'other' && (
                <TextField
                  fullWidth
                  name="otherLanguage"
                  value={formData.otherLanguage}
                  onChange={handleInputChange}
                  placeholder="Enter your primary language"
                  sx={{ mt: 2 }}
                  required
                />
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                7. Signature
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Employee Name"
                    name="employeeName"
                    value={formData.employeeName}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Employee Signature
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ 
                      bgcolor: 'white',
                      mb: 2
                    }}
                  >
                    <canvas
                      ref={canvasRef}
                      style={{
                        width: '100%',
                        height: '150px',
                        touchAction: 'none'
                      }}
                    />
                  </Paper>
                  <Button
                    variant="outlined"
                    onClick={() => signaturePadRef.current?.clear()}
                    sx={{ mb: 2 }}
                  >
                    Clear Signature
                  </Button>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Date"
                    name="signatureDate"
                    type="date"
                    value={formData.signatureDate}
                    onChange={handleInputChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    required
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card sx={{ mb: 3 }}>
  <CardContent>
    <FormControlLabel
      control={
        <Checkbox
          checked={formData.legalAcknowledgment}
          onChange={handleInputChange}
          name="legalAcknowledgment"
          required
        />
      }
      sx={{
        alignItems: 'flex-start',
        '& .MuiFormControlLabel-label': {
          mt: 0.5
        }
      }}
      label={
        <Box>
          <Typography variant="subtitle2" color="text.primary" gutterBottom>
            Legal Acknowledgment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            On this day I have been notified of my pay rate, overtime rate (if eligible), allowances, and designated pay day. I told my employer what my primary language is. I understand that this is a legal document and by signing above, I acknowledge that all information provided is true and accurate.
          </Typography>
        </Box>
      }
    />
  </CardContent>
</Card>

<Button
  type="submit"
  variant="contained"
  color="primary"
  fullWidth
  size="large"
  disabled={isLoading || !formData.legalAcknowledgment}
  sx={{ mb: 4 }}
>
  {isLoading ? (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <CircularProgress size={24} sx={{ mr: 1 }} />
      Processing...
    </Box>
  ) : (
    'Submit Form'
  )}
</Button>
       
        </form>
      </Box>

      <Drawer
        variant="temporary"
        anchor={isMobile ? 'bottom' : 'right'}
        open={isPreviewOpen}
        onClose={togglePreview}
        ModalProps={{ keepMounted: true }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.3)' } }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 20,
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : getDrawerWidth(),
            height: isMobile ? '80vh' : '100vh',
            boxSizing: 'border-box'
          }
        }}
      >
        <Box sx={{ 
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Typography variant="h6">
              Form Preview
            </Typography>
            <IconButton onClick={togglePreview} edge="end" aria-label="Close preview">
              <Close />
            </IconButton>
          </Box>
          
          <Box 
            sx={{ 
              flexGrow: 1,
              border: 1,
              borderColor: 'grey.300',
              borderRadius: 1,
              overflow: 'hidden',
              height: isMobile ? 'calc(80vh - 80px)' : 'calc(100vh - 120px)',
            }}
          > 
            <iframe
              ref={iframeRef}
              src="/static-forms/wage_rate_notice.pdf"
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title="Wage Rate Notice Form Preview"
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default WageRateNoticeForm;
